import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, X, UsersRound } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';

export default function Grupos() {
  const [groups, setGroups] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [nombre, setNombre] = useState('');
  const [selectedVendors, setSelectedVendors] = useState([]);

  const fetchData = async () => {
    try {
      const [groupsData, vendorsData] = await Promise.all([api.getGroups(), api.getVendors()]);
      setGroups(groupsData); setVendors(vendorsData);
    } catch (error) { /* handled */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const toggleVendor = (vendorId) => {
    setSelectedVendors(prev => prev.includes(vendorId) ? prev.filter(id => id !== vendorId) : [...prev, vendorId]);
  };

  const handleCreate = async () => {
    if (!nombre || selectedVendors.length === 0) { toast.error('Completa el nombre y selecciona al menos un vendedor'); return; }
    try { await api.createGroup({ nombre, vendedores: selectedVendors }); toast.success('Grupo creado'); setShowCreateDialog(false); setNombre(''); setSelectedVendors([]); fetchData(); }
    catch (error) { const detail = error.response?.data?.detail; toast.error(typeof detail === 'string' ? detail : 'Error al crear grupo'); }
  };

  const handleDelete = async (groupId) => {
    if (!window.confirm('Eliminar este grupo?')) return;
    try { await api.deleteGroup(groupId); toast.success('Grupo eliminado'); fetchData(); }
    catch (error) { toast.error('Error al eliminar grupo'); }
  };

  const getVendorNames = (vendorIds) => vendorIds.map(id => vendors.find(v => v.id === id)?.nombre || 'Desconocido').join(', ');

  return (
    <div className="space-y-6" data-testid="grupos">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]">Grupos</h1>
          <p className="text-[13px] text-gray-500 mt-0.5">Organizacion de vendedores por grupos</p>
        </div>
        <button onClick={() => setShowCreateDialog(true)} className="btn-primary flex items-center gap-2" data-testid="create-group-btn"><Plus size={16} /> Crear Grupo</button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="text-gray-500 text-[13px]">Cargando...</div></div>
      ) : groups.length === 0 ? (
        <div className="mac-card p-8 text-center"><UsersRound size={40} className="mx-auto text-gray-200 mb-3" /><p className="text-gray-500">No hay grupos creados</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map(group => (
            <div key={group.id} className="mac-card p-5">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-[15px] font-semibold text-[#0A0A0A]">{group.nombre}</h3>
                <button onClick={() => handleDelete(group.id)} className="btn-ghost p-1.5 text-gray-400 hover:text-[#EF4444]" data-testid={`delete-group-${group.id}`}><Trash2 size={16} /></button>
              </div>
              <div className="space-y-3">
                <div><div className="metric-label">Vendedores</div><p className="text-[13px] text-gray-600">{getVendorNames(group.vendedores)}</p></div>
                <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-200">
                  <div><div className="metric-label">Total Ventas 2026</div><div className="text-xl font-bold text-[#0A0A0A]">{group.services_2026 || 0}</div></div>
                  <div><div className="metric-label">Este Mes</div><div className="text-xl font-bold text-[#0A0A0A]">{group.services_month || 0}</div></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="mac-popover max-w-md p-0">
          <DialogHeader className="px-5 py-4 border-b border-gray-200"><DialogTitle className="text-[15px] font-semibold text-[#0A0A0A]">Crear Grupo</DialogTitle></DialogHeader>
          <div className="p-5 space-y-4">
            <div><label className="form-label">Nombre del Grupo</label><input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} className="form-input" placeholder="Ej: Equipo A" data-testid="group-nombre" /></div>
            <div>
              <label className="form-label mb-2">Seleccionar Vendedores</label>
              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1">
                {vendors.map(vendor => (
                  <label key={vendor.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-sm cursor-pointer">
                    <Checkbox checked={selectedVendors.includes(vendor.id)} onCheckedChange={() => toggleVendor(vendor.id)} className="rounded-sm" data-testid={`vendor-checkbox-${vendor.id}`} />
                    <span className="text-[13px] text-gray-700">{vendor.nombre}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowCreateDialog(false)} className="btn-secondary">Cancelar</button>
              <button onClick={handleCreate} className="btn-primary" data-testid="save-group-btn">Crear</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
